import { Link } from "react-router-dom";
import { Plane, ArrowRight, Users, Newspaper } from "lucide-react";
import { PageHeader, vibrantTheme } from "../components/common";
import { GuideTeaser } from "../components/ArticleSections";
import { GUIDES } from "../content/guides";
import { SITE } from "../lib/site";

const CATEGORIES = {
  air: {
    icon: Plane, accent: "#2563eb", title: "Air Travel",
    subtitle: "Airlines, airports, flights, terminals, routes and everything aviation — our founding vertical.",
    sections: [
      { label: "Airports Guide", desc: "85,000+ airports with terminals, serving airlines and community reviews.", to: "/airports" },
      { label: "Airlines Information", desc: "6,000+ airlines with fleets, hubs, routes and loyalty programs.", to: "/airlines" },
      { label: "Aircraft", desc: "Aircraft types, manufacturers and the fleets that fly them.", to: "/aircraft" },
      { label: "Routes & Destinations", desc: "Interactive world route map — explore who flies where.", to: "/map" },
      { label: "Flight Status & Alerts", desc: "Live flight lookups, saved flights and delay alerts in My Travel Hub.", to: "/travel-hub" },
      { label: "Mileage Programs & Credit Cards", desc: "Loyalty programs, miles and co-branded travel cards.", to: "/loyalty" },
      { label: "Community & Reviews", desc: "Traveller photos, stories and discussions from around the world.", to: "/community" },
      { label: "Complaints Forum", desc: "Share experiences and give your voice to the travel community.", to: "/complaints" },
      { label: "Aviation News", desc: "Travel news and alerts from the world of airlines and airports.", to: "/articles" },
    ],
  },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function CategoryHub({ category }) {
  const cfg = CATEGORIES[category];
  const Icon = cfg.icon;
  const hasComing = cfg.sections.some((s) => !s.to);

  return (
    <div data-testid={`category-hub-${category}`}>
      <PageHeader
        eyebrow="TravelPulse Category"
        title={cfg.title}
        subtitle={cfg.subtitle}
        entityCode={cfg.title}
      />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <GuideTeaser guide={GUIDES[category]} />
        <div className="flex items-center gap-3 mb-8">
          <span className="p-3 text-white" style={{ backgroundColor: cfg.accent }}><Icon className="w-7 h-7" /></span>
          <div>
            <h2 className="font-heading font-900 text-2xl">Explore {cfg.title}</h2>
            {hasComing && <p className="text-sm text-muted-foreground">Sections marked “Coming soon” are being built as TravelPulse expands beyond aviation.</p>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cfg.sections.map((s) => {
            const id = `hub-section-${slug(s.label)}`;
            return s.to ? (
              <Link key={s.label} to={s.to} data-testid={id}
                className="group border border-zinc-300 bg-white p-5 hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading font-700 text-lg">{s.label}</h3>
                  <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </Link>
            ) : (
              <div key={s.label} data-testid={id} className="border border-dashed border-zinc-300 bg-zinc-50 p-5">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading font-700 text-lg text-zinc-600">{s.label}</h3>
                  <span className="text-xs font-mono uppercase tracking-wide border px-2 py-0.5" style={{ color: cfg.accent, borderColor: cfg.accent }}>Coming soon</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <Link to="/community" data-testid="hub-cta-community"
            className="flex items-center gap-4 border border-zinc-300 text-white p-6 hover:border-primary transition-colors"
            style={{ backgroundColor: vibrantTheme(`${category}-community`).bg }}>
            <Users className="w-8 h-8 shrink-0" style={{ color: vibrantTheme(`${category}-community`).accent }} />
            <div>
              <div className="font-heading font-900 text-lg">Join the {SITE.name} community</div>
              <div className="text-sm text-white/85">Ask questions, share tips and help fellow travellers.</div>
            </div>
          </Link>
          <Link to="/articles" data-testid="hub-cta-news"
            className="flex items-center gap-4 border border-zinc-300 bg-white p-6 hover:border-primary transition-colors">
            <Newspaper className="w-8 h-8 shrink-0 text-primary" />
            <div>
              <div className="font-heading font-900 text-lg">Travel news & alerts</div>
              <div className="text-sm text-muted-foreground">Stay updated with the latest from every mode of travel.</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
